import { SecretStorage } from "./secrets.js";
import { configSchema, Config, AppConfig, StoredConfig } from "./schemas.js";
import { Mutex } from "async-mutex";
import { logger } from "../logger.js";

export class ConfigStore {
  private mutex = new Mutex();

  constructor(
    private readonly secretStore: SecretStorage<StoredConfig>,
    private readonly currentAppConfig: AppConfig,
    private readonly storageKey: string = "secrets",
  ) {}

  async init() {
    const exists = await this.secretStore.exists(this.storageKey);
    if (!exists) {
      logger.info("No config found, setting defaults");
      await this.save({ api: this.currentAppConfig.demoSandbox.api });
    }
  }

  async load(): Promise<Readonly<Config>> {
    const storedConfig = await this.secretStore.get(this.storageKey);
    if (!storedConfig) {
      throw new Error("Invalid stored config found");
    }
    const config = { ...storedConfig, app: this.currentAppConfig };
    return configSchema.parse(config);
  }

  private async save(config: Config) {
    // ! Never save the app config, it should be loaded via code
    const { app, ...configWithoutApp } = config;
    await this.secretStore.set(this.storageKey, configWithoutApp);
  }

  async update(config: Partial<Config>) {
    try {
      await this.mutex.runExclusive(async () => {
        const currentConfig = await this.load();
        await this.save({ ...currentConfig, ...config });
      });
    } catch (error) {
      logger.error("Error updating config", error);
      throw error;
    }
  }

  async clear() {
    await this.mutex.runExclusive(async () => {
      await this.secretStore.delete(this.storageKey);
    });
  }
}
